/*
 * File: SupportContent.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 9:02 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useEffect, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { generateShortFormContentPlaceholder } from '../../utilities/placeholder';
import { isValidNode } from '../../utilities/parser';

// Services
import { recordAction } from '../../services/action';

// Components
import { Typography } from '../Typography';
import { LocaleLink } from '../LocaleLink';

// Constants
import { AVAILABLE_I18N_NAMESPACES, INTERNAL_CONTENT_TYPES, USER_ACTIONS } from '../../../constants/base.constants';

// Styles
import * as S from './SupportContent.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const SupportContent = memo(({
	className, item, isPlaceholder, index
}) => {

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get mantra content
	const mantraContent = useMemo(() => ReactParser.parseWithInstructions(item?.content_locales?.[locale.localeBaseId], isValidNode, ParsingInstructions), [locale, item]);
	const attributionContent = useMemo(() => item?.attribution_locales?.[locale.localeBaseId], [locale, item]);

	// Create video reference
	const videoRef = useRef();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Generate placeholder
	const placeholder = useMemo(() => generateShortFormContentPlaceholder(index), [index]);

	// Handle action for support card click
	const recordCardClick = () => {

		// Record action
		recordAction({
			action: USER_ACTIONS.SUPPORT_CARD_CLICK,
			urlPath: window.location.pathname,
			context: {
				support_id: item.id
			}
		});
	};

	// Handle actions on video reference update
	useEffect(() => {

		// Set video event listeners
		videoRef?.current?.addEventListener('play', () => {

			// Record action
			recordAction({
				action: USER_ACTIONS.SUPPORT_CARD_VIDEO_PLAY,
				urlPath: window.location.pathname,
				context: {
					support_id: item.id
				}
			});
		});

		// Handle actions on dismount
		return () => { // Unbind the event listener on clean up
			videoRef?.current?.removeEventListener('play');
		};

	}, [videoRef]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Create attribution
	const attributionContainer = useMemo(() => (
		<>
			{attributionContent && (
				<S.AttributionLabel>
					<Typography
						tag="p"
						weight="semibold"
						center
						variation="3"
					>
						{attributionContent}
					</Typography>
				</S.AttributionLabel>
			)}
			{item?.story && (
				<LocaleLink to={`/story/${item.story.link_component}`}>
					<S.AttributionLabel>
						<Typography
							tag="p"
							weight="semibold"
							center
							variation="3"
						>
							<Trans t={t} i18nKey="storyNumber">
								Story #
								{{ number: item.story.number }}
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
					</S.AttributionLabel>
				</LocaleLink>
			)}
			{item?.question && (
				<LocaleLink to={`/story/${item.question.slug}`}>
					<S.AttributionLabel>
						<Typography
							tag="p"
							weight="semibold"
							center
							variation="3"
						>
							<Trans t={t} i18nKey="questionNumber">
								Question #
								{{ number: item.question.number }}
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
					</S.AttributionLabel>
				</LocaleLink>
			)}
			{item?.message && (
				<LocaleLink to={`/message/${item.message.slug}`}>
					<S.AttributionLabel>
						<Typography
							tag="p"
							weight="semibold"
							center
							variation="3"
						>
							<Trans t={t} i18nKey="messageNumber">
								Message #
								{{ number: item.message.number }}
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
					</S.AttributionLabel>
				</LocaleLink>
			)}
		</>
	), [attributionContent, item.story, item.question, item.message, t]);

	// Render component
	switch (item?.internal_content_type) {
		case INTERNAL_CONTENT_TYPES.MANTRA:
			return (
				<S.MantraWrapper className={className} onClick={recordCardClick}>
					<Typography tag="h4" weight="bold" center>{mantraContent}</Typography>
					<S.AttributionSection>{attributionContainer}</S.AttributionSection>
				</S.MantraWrapper>
			);
		case INTERNAL_CONTENT_TYPES.SUPPORT_IMAGE:
			return (
				<S.ImageWrapper className={className} onClick={recordCardClick}>
					<S.Image src={item.image_url} title={mantraContent} alt={mantraContent} />
					<S.AttributionSection>{attributionContainer}</S.AttributionSection>
				</S.ImageWrapper>
			);
		case INTERNAL_CONTENT_TYPES.SUPPORT_VIDEO:
			return (
				<S.VideoWrapper className={className} onClick={recordCardClick}>
					<S.Video
						playsinline
						preload="metadata"
						controls
						controlsList="nodownload"
						poster={item.video_poster_url}
						ref={videoRef}
					>
						<source src={item.video_url} type="video/mp4" />
					</S.Video>
					<S.AttributionSection>{attributionContainer}</S.AttributionSection>
				</S.VideoWrapper>
			);
		default:
			if (isPlaceholder) {
				return (
					<S.MantraWrapper className={className}>
						<Typography tag="h4" weight="bold" className="shimmer shimmer-element" center>{placeholder}</Typography>
					</S.MantraWrapper>
				);
			}
			return null;
	}
});


/**
 * Configuration
 */

SupportContent.displayName = 'SupportContent';
SupportContent.propTypes = {
	className: PropTypes.string,
	item: PropTypes.shape(),
	isPlaceholder: PropTypes.bool,
	index: PropTypes.number
};
SupportContent.defaultProps = {
	className: null,
	item: {},
	isPlaceholder: false,
	index: 0
};
